import { Intent } from "@blueprintjs/core";
import { LinkButton } from "components/buttons";
import { DateToLocal } from "components/dates";
import { Filter, FilterButton } from "components/filtering";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { AlertConfirm, NotificationToaster } from "components/notifications";
import { Tooltip } from "components/tooltip";
import { UserAccess } from "components/user-access";
import { useDebounce } from "hooks/useDebounce";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DocumentsService } from "services/DocumentsService";
import { SetFiltering } from "state/actions";
import { parseBytesTo } from "utils/parseBytesTo";

export function DocumentTypesListing(props) {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const filterName = 'documentTypes';

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useDispatch();

    const [documentOptions, setDocumentOptions] = useState({
        documentAreas: []
    });

    const tableHeaders = [
        "Name",
        "Area",
        "No. Of Docs",
        "Total Size",
        "Date to be Deleted",
        "Delete",
        "Recover"
    ];
    const sortableHeaders = [
        { headerName: "Name", sortName: "NAME" },
        { headerName: "Area", sortName: "AREA" },
        { headerName: "No. Of Docs", sortName: "NUMBEROFDOCUMENTS" },
        { headerName: "Total Size", sortName: "TOTALBYTESIZE" },
        { headerName: "Date to be Deleted", sortName: "DATETOBEDELETED" }
    ];

    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [typeToDelete, setTypeToDelete] = useState();
    const [typeToRecover, setTypeToRecover] = useState();

    const selectedFilters = useSelector(state => state.filters[filterName]);

    if (!selectedFilters) {
        dispatch(SetFiltering(filterName, []))
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function getAreaName(area) {
        return documentOptions.documentAreas.find(a => a.key === area)?.name;
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete) {
            if (
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentTableSortDir.current === sortedDir &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentAppliedFilters.current === appliedFilters &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentTableSortBy.current = sortedBy;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;
            currentAppliedFilters.current = appliedFilters;

            setIsLoading(true);

            const searchRequest = {
                requestCount: defaultRecordSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: (currentAppliedFilters.current ?? []).map(f => ({
                    key: f.name,
                    ...f,
                })),
                searchTerm: debouncedSearchTerm,
            };

            DocumentsService.getDocumentTypes(searchRequest).then(
                (r) => {
                    let data = [];

                    if (r?.data != null) {
                        data = r.data.map((d) => {
                            return [
                                <div key={d.id}>{d.featureId ? <Tooltip content="This document type has been created by a Vision feature and cannot be changed.">{d.name}</Tooltip> : <Link to={`/document-settings/types/${d.id}`}>{`${d.name}`}</Link>}</div>,
                                getAreaName(d.area),
                                d.numberOfDocuments,
                                parseBytesTo(d.totalByteSize).auto(),
                                <DateToLocal
                                    key={d.id}
                                    format="DD/MM/YYYY"
                                >{d.dateToBeDeleted}</DateToLocal>,
                                <LinkButton key={d.id} minimal={true} large={false} onClick={() => setTypeToDelete(d)} intent="delete" disabled={d.dateToBeDeleted || d.featureId} icon="archive" />,
                                <LinkButton key={d.id} minimal={true} large={false} onClick={() => setTypeToRecover(d)} intent="primary" disabled={!d.dateToBeDeleted} icon="reset" />
                            ];
                        });
                    }

                    setTableRows(data);
                    setTotalRecords(r.totalCount);
                    setIsLoading(false);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve document types, please try again"
                    );
                }
            );
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete,
        appliedFilters
    ]);

    useEffect(() => {
        DocumentsService.getDocumentOptions()
            .then(o => {
                setDocumentOptions(o)
                setPageFilters([
                    {
                        name: 'AREA',
                        displayName: 'Area',
                        items: o.documentAreas.map(a => ({
                            id: a.key,
                            name: a.name
                        }))
                    }
                ])
                setInitalSetupComplete(true);
            });
    }, []);

    function deleteType() {
        if (!typeToDelete) {
            return;
        }

        setTypeToDelete(null);
        setIsLoading(true);
        DocumentsService.deleteDocumentType(typeToDelete.id)
            .then(() => {
                fetchTableData(true);
            })
    }

    function recoverType() {
        if (!typeToRecover) {
            return;
        }

        setTypeToRecover(null);
        setIsLoading(true);
        DocumentsService.recoverDocumentType(typeToRecover.id)
            .then(() => {
                fetchTableData(true);
            })
    }

    return (
        <>
            <div className="spacer-bottom">
                <p>This page allows you to manage your document types, some types are added by feature to allow documents to be collated together. To add custom types hit the new document type button to get started.</p>
            </div>
            <div className="button-row spacer-bottom">
                <LinkButton
                    href={`/document-settings/types/new`}
                    text={"New Document Type"}
                    intent="primary"
                    large
                    id="new-document-type-button"
                    icon="plus"
                    disabled={isLoading}
                />
                <UserAccess
                    perform='Account:Manage'
                    yes={() => <LinkButton
                        href={`/account/data-retention`}
                        text={"Edit Data Retention"}
                        intent="primary"
                        large
                        id="data-retention-link"
                        disabled={isLoading}
                    />}
                />
            </div>
            <div className="filter-controls">
                <div className="inline-items spacer-bottom-small">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search Document Types"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="document-type-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>                    
                </div>
                <Filter
                    filterName={filterName}
                    visible={showFilters}
                    filters={pageFilters}
                    onUpdate={onFilterChange}
                    onClickOutside={onToggleFilter}
                />
            </div>
            <ListingTable
                showPerPage={false}
                pageSize={defaultRecordSize}
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onTableSort}
            />
            <AlertConfirm
                title="Delete Document Type"
                isOpen={!!typeToDelete}
                onConfirm={deleteType}
                onCancel={() => setTypeToDelete(null)}
                confirmButtonIntent="danger"
                confirmButtonText="Delete">
                <p>Are you sure you want to delete this document type? This action is only reversible for a limited time. After that, the document type and all associated documents will be permanently removed from Vision.</p>
            </AlertConfirm>
            <AlertConfirm
                title="Recover Document Type"
                isOpen={!!typeToRecover}
                onConfirm={recoverType}
                onCancel={() => setTypeToRecover(null)}
                confirmButtonIntent="primary"
                confirmButtonText="Recover">
                <p>Are you sure you want to recover this document type?</p>
            </AlertConfirm>
        </>
    );
}